import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, AlertCircle } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [remember, setRemember] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async e => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      await login(email, pass, remember);
      navigate('/');
    } catch {
      setErr('Credenziali non valide. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background orbs */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        <div style={{
          position: 'absolute', width: 500, height: 500, borderRadius: '50%',
          background: 'var(--cyan)', filter: 'blur(140px)', opacity: 0.07,
          top: '-10%', left: '-10%',
        }} />
        <div style={{
          position: 'absolute', width: 400, height: 400, borderRadius: '50%',
          background: 'var(--purple)', filter: 'blur(140px)', opacity: 0.07,
          bottom: '-10%', right: '-5%',
        }} />
      </div>

      <div style={{
        position: 'relative', zIndex: 1,
        background: 'rgba(14,14,26,0.85)',
        backdropFilter: 'blur(24px)',
        border: '1px solid var(--border)',
        borderRadius: 20,
        padding: '40px 36px',
        width: '100%',
        maxWidth: 400,
        boxShadow: 'var(--shadow-lg)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: 'linear-gradient(135deg, var(--cyan), var(--purple))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 0 30px rgba(0,212,255,0.3)',
          }}>
            <span style={{ fontWeight: 800, fontSize: 22, color: 'var(--on-accent)' }}>E</span>
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
            EdenCRM
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Accedi al tuo workspace
          </p>
        </div>

        {err && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--red-dim)', border: '1px solid rgba(244,63,94,0.25)',
            borderRadius: 8, padding: '10px 12px', marginBottom: 16,
          }}>
            <AlertCircle size={14} color="var(--red)" style={{ flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: 'var(--red)' }}>{err}</span>
          </div>
        )}

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Email
            </label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              required className="eden-input"
              placeholder="admin@edencrm.local"
              autoComplete="username" aria-invalid={!!err}
              style={err ? { borderColor: 'var(--red)' } : undefined}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Password
            </label>
            <input
              type="password" value={pass} onChange={e => setPass(e.target.value)}
              required className="eden-input"
              placeholder="••••••••"
              autoComplete="current-password" aria-invalid={!!err}
              style={err ? { borderColor: 'var(--red)' } : undefined}
            />
          </div>
          <div role="checkbox" aria-checked={remember} tabIndex={0}
            onClick={()=>setRemember(r=>!r)}
            onKeyDown={e=>{ if(e.key===' '||e.key==='Enter'){ e.preventDefault(); setRemember(r=>!r); } }}
            style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', userSelect:'none', marginTop:2, width:'fit-content' }}>
            <div style={{
              width:18, height:18, borderRadius:5, flexShrink:0,
              border: remember ? 'none' : '1.5px solid var(--border)',
              background: remember ? 'var(--cyan)' : 'transparent',
              display:'flex', alignItems:'center', justifyContent:'center',
              transition:'background .15s',
            }}>
              {remember && (
                <svg width={11} height={9} viewBox="0 0 11 9" fill="none">
                  <path d="M1 4l3 3 6-6" stroke="var(--on-accent)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <span style={{ fontSize:13, color:'var(--text-muted)' }}>Resta connesso</span>
          </div>

          <button type="submit" disabled={loading} className="eden-btn-primary" style={{ marginTop: 6, justifyContent: 'center', padding: '11px 18px', fontSize: 14 }}>
            {loading ? (
              <span style={{ opacity: 0.7 }}>Accesso in corso...</span>
            ) : (
              <><LogIn size={15} /> Accedi</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
